<script setup lang="ts">
import { LazyConfirmModal } from '#components'
import {
  SUGGESTION_STATUSES,
  canModerateSuggestions,
  getSuggestionCategory,
  suggestionExcerpt,
  type Suggestion,
  type SuggestionStatus,
  type SuggestionVoteResult
} from '~~/shared/utils/suggestions'

const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()
const overlay = useOverlay()
const confirmModal = overlay.create(LazyConfirmModal)

const id = route.params.id as string

const { data, error, refresh } = await useFetch<{ suggestion: Suggestion }>(`/api/suggestions/${id}`, {
  key: `suggestion-${id}`
})

if (error.value || !data.value?.suggestion) {
  const statusCode = !data.value || error.value?.statusCode === 404 ? 404 : (error.value?.statusCode ?? 500)
  throw createError({ statusCode, statusMessage: statusCode === 404 ? 'Suggestion not found' : 'Could not load suggestion', fatal: true })
}

const suggestion = computed(() => data.value!.suggestion)

const authorName = computed(() => suggestion.value.author.display_name || suggestion.value.author.username || 'Deleted user')
const isAuthor = computed(() => !!authStore.userId && authStore.userId === suggestion.value.author.id)
// Admins and managers moderate; moderators are regular members here
const isBoardModerator = computed(() => canModerateSuggestions(authStore.role))
const canEdit = computed(() => isBoardModerator.value || (isAuthor.value && suggestion.value.status === 'open'))
const canDelete = computed(() => isBoardModerator.value || isAuthor.value)
const canSetStatus = isBoardModerator

// User-written and often thin: keep detail pages out of the index, but let
// crawlers follow links (author pages, the board)
useRobotsRule('noindex, follow')

useSeoMeta({
  title: () => suggestion.value.title,
  ogTitle: () => suggestion.value.title,
  description: () => suggestion.value.description
    ? suggestionExcerpt(suggestion.value.description)
    : `A ${getSuggestionCategory(suggestion.value.category).label.toLowerCase()} suggestion for mychar.gg. Vote and comment on the suggestions board.`
})

defineOgImageComponent('Saas', {
  title: suggestion.value.title,
  description: 'A suggestion on the mychar.gg board'
})

/** Replace the suggestion (data is a shallow ref). */
function patchSuggestion(patch: Partial<Suggestion>) {
  if (!data.value) return
  data.value = { suggestion: { ...data.value.suggestion, ...patch } }
}

function applyVote(result: SuggestionVoteResult) {
  patchSuggestion(result)
}

async function openEditor() {
  const SuggestionEditorModal = defineAsyncComponent(() => import('~/components/modals/suggestions/SuggestionEditorModal.vue'))
  const savedId = await overlay.create(SuggestionEditorModal).open({
    suggestion: {
      id: suggestion.value.id,
      title: suggestion.value.title,
      description: suggestion.value.description,
      category: suggestion.value.category
    }
  }).result
  if (savedId) await refresh()
}

async function deleteSuggestion() {
  const confirmed = await confirmModal.open({
    title: 'Delete suggestion?',
    description: 'Its votes and comments are deleted too. This cannot be undone.',
    confirmText: 'Delete'
  }).result
  if (!confirmed) return

  try {
    await $fetch(`/api/suggestions/${id}`, { method: 'DELETE' })
    toast.add({ title: 'Suggestion deleted', color: 'success', icon: 'i-lucide-check', duration: 2500 })
    await navigateTo('/suggestions')
  } catch (err) {
    toast.add({
      title: 'Could not delete suggestion',
      description: fetchErrorMessage(err),
      color: 'error',
      icon: 'i-lucide-alert-circle'
    })
  }
}

const statusItems = SUGGESTION_STATUSES.map(s => ({ label: s.label, value: s.value, icon: s.icon }))
const statusSaving = ref(false)

const status = computed({
  get: () => suggestion.value.status,
  set: (value: SuggestionStatus) => updateStatus(value)
})
const statusIcon = computed(() => statusItems.find(s => s.value === status.value)?.icon)

async function updateStatus(value: SuggestionStatus) {
  if (value === suggestion.value.status) return
  statusSaving.value = true
  try {
    await $fetch(`/api/suggestions/${id}/status`, { method: 'PUT', body: { status: value } })
    patchSuggestion({ status: value })
  } catch (err) {
    toast.add({
      title: 'Could not change the status',
      description: fetchErrorMessage(err),
      color: 'error',
      icon: 'i-lucide-alert-circle'
    })
  } finally {
    statusSaving.value = false
  }
}
</script>

<template>
  <div class="min-h-screen">
    <UContainer class="pb-24 pt-28 sm:pt-32">
      <UBreadcrumb
        :items="[
          { label: 'Help center', to: '/help' },
          { label: 'Suggestions', to: '/suggestions' },
          { label: suggestion.title }
        ]"
        class="mb-6"
      />

      <article class="rounded-2xl border border-default bg-elevated/40 p-5 shadow-(--surface-shadow) sm:p-6">
        <div class="flex gap-4 sm:gap-5">
          <SuggestionVote
            :suggestion-id="suggestion.id"
            :score="suggestion.score"
            :upvotes="suggestion.upvotes"
            :downvotes="suggestion.downvotes"
            :my-vote="suggestion.my_vote"
            @voted="applyVote"
          />

          <div class="min-w-0 flex-1">
            <h1 class="text-xl font-bold text-highlighted wrap-break-word sm:text-2xl">
              {{ suggestion.title }}
            </h1>

            <SuggestionBadges
              :category="suggestion.category"
              :status="suggestion.status"
              show-open
              class="mt-3"
            />

            <div class="mt-4 flex items-center gap-2 text-sm text-muted">
              <UAvatar
                :src="suggestion.author.avatar_url || undefined"
                :alt="authorName"
                size="xs"
              />
              <NuxtLink
                v-if="suggestion.author.username"
                :to="`/${suggestion.author.username}`"
                class="font-medium text-toned hover:underline"
              >
                {{ authorName }}
              </NuxtLink>
              <span v-else>{{ authorName }}</span>
              <span class="text-dimmed">·</span>
              <NuxtTime
                :datetime="suggestion.created_at"
                relative
                locale="en-US"
                class="text-dimmed"
              />
            </div>

            <p
              v-if="suggestion.description"
              class="mt-5 whitespace-pre-line wrap-break-word text-toned"
            >
              {{ suggestion.description }}
            </p>
            <p
              v-else
              class="mt-5 text-sm italic text-dimmed"
            >
              No details added.
            </p>
          </div>
        </div>

        <!-- Owner and staff actions -->
        <div
          v-if="canEdit || canDelete || canSetStatus"
          class="mt-6 flex flex-wrap items-center gap-2 border-t border-default pt-4"
        >
          <USelect
            v-if="canSetStatus"
            v-model="status"
            :items="statusItems"
            :icon="statusIcon"
            :loading="statusSaving"
            value-key="value"
            size="sm"
            class="w-40"
            aria-label="Status"
          />
          <div class="flex-1" />
          <UButton
            v-if="canEdit"
            label="Edit"
            icon="i-lucide-pencil"
            color="neutral"
            variant="ghost"
            size="sm"
            @click="openEditor"
          />
          <UButton
            v-if="canDelete"
            label="Delete"
            icon="i-lucide-trash-2"
            color="error"
            variant="ghost"
            size="sm"
            @click="deleteSuggestion"
          />
        </div>
      </article>

      <SuggestionComments
        :suggestion-id="suggestion.id"
        class="mt-10"
        @count="patchSuggestion({ comment_count: $event })"
      />
    </UContainer>
  </div>
</template>
